import React, { useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import DocumentTitle from '../../components/documentTitle/documentTitle';
import { withTranslation, WithTranslation } from 'react-i18next';
import {
  setOther as setOtherAction,
  setIndustry as setIndustryAction,
} from '../../redux/actions/intent';
import {
  changeViewToProduct as changeViewToProductAction,
  navigate as navigateAction,
} from '../../redux/actions/router';
import Industry from '../../components/industry/industry';
import ContentView from '../../components/contentView/contentView';
import { getIndustries } from '../../modules/industries';
import { AppState } from '../../redux/reducers';
import { AppDispatch, ViewOwnProps } from '../../definitions/types';
import { trackDWH } from '../../modules/tracking';

interface StateProps {
  industry: string;
  jumpToIndustryView: boolean;
}

interface DispatchProps {
  setIndustry: typeof setIndustryAction;
  setOther: typeof setOtherAction;
  changeViewToProduct: typeof changeViewToProductAction;
  navigate: typeof navigateAction;
}

type IndustryViewProps = StateProps & DispatchProps & ViewOwnProps & WithTranslation;

export class IndustryView extends React.Component<IndustryViewProps, {}> {
  mountTime: number = Date.now();

  constructor(props: IndustryViewProps) {
    super(props);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.skipIndustryView = this.skipIndustryView.bind(this);
  }

  componentDidMount(): void {
    trackDWH('industry-view', 'mount', {
      auto: true,
    });
    this.mountTime = Date.now();
  }

  componentWillUnmount(): void {
    trackDWH('industry-view', 'unmount', {
      auto: true,
      mountTimeMs: Date.now() - this.mountTime,
    });
  }

  public render(): JSX.Element {
    const { setIndustry, setOther, industry, changeViewToProduct, jumpToIndustryView } = this.props;

    const industries = getIndustries(this.props.t);

    return (
      <DocumentTitle title={this.props.t('signup-flow.document-title.industry')}>
        <ContentView>
          <Industry
            backButtonLabel={this.props.t('signup-flow.misc.backButton.label')}
            cta={this.props.t('signup-flow.intent.cta')}
            titleText={this.props.t('signup-flow.industry.view.name')}
            headline={this.props.t('signup-flow.intent.headline')}
            skipLabel={this.props.t('signup-flow.intent.skip-label')}
            selectDefault={this.props.t('signup-flow.industry.select-default')}
            industry={industry}
            industries={industries}
            placeholder={this.props.t('signup-flow.industry.placeholder')}
            onSelect={setIndustry}
            setOther={setOther}
            onBackClick={changeViewToProduct}
            onSubmit={this.handleSubmit}
            onSkip={this.skipIndustryView}
            jumpToIndustryView={jumpToIndustryView}
          />
        </ContentView>
      </DocumentTitle>
    );
  }
  private handleSubmit(): void {
    this.props.navigate(this.props.route.path || '');
  }

  private skipIndustryView(): void {
    this.props.setIndustry('');
    this.handleSubmit();
  }
}

const mapStateToProps = (state: AppState): StateProps => {
  return {
    industry: state.intent.industry,
    jumpToIndustryView: state.intent.jumpToIndustryView,
  };
};

const mapDispatchToProps = (dispatch: AppDispatch): DispatchProps => {
  return bindActionCreators(
    {
      changeViewToProduct: changeViewToProductAction,
      navigate: navigateAction,
      setIndustry: setIndustryAction,
      setOther: setOtherAction,
    },
    dispatch
  );
};

const TranslatedIndustryView = withTranslation()(IndustryView);

const ConnectedIndustryView = (ownProps: ViewOwnProps): JSX.Element => {
  const stateProps = useSelector(mapStateToProps, shallowEqual);
  const dispatch = useDispatch<AppDispatch>();
  const dispatchProps = useMemo(() => mapDispatchToProps(dispatch), [dispatch]);

  return <TranslatedIndustryView {...ownProps} {...stateProps} {...dispatchProps} />;
};

export default React.memo(ConnectedIndustryView);
